import { Module } from 'vuex';
import Jitsi from './jitsi';
import { RootState, RootGettersReturns } from '../types/index';
import {
  SessionState,
  SessionActionsKeys,
  SessionMutationsKeys,
  SessionGettersKeys,
  SessionActionsPayload,
  SessionMutationsPayload,
  SessionStatus,
} from '../types/session';
import {
  SocketActionsKeys,
  JoinSessionResponse,
  JoinSessionEmit,
  StartSessionEmit,
  PauseSessionEmit,
  LeaveSessionEmit,
  SessionStartedResponse,
  SessionUsersEmit,
  SessionUsersResponse,
  DeleteSessionEmit,
} from '../types/socket';
import { JitsiMutationsKeys } from '../types/jitsi';
import API from '@/plugins/axios';
import { BoardActionsKeys } from '../types/board';
import { StageDBType, StageMutationsKeys, StageActionsKeys } from '../types/stageState';
import { ElementActionsKeys } from '../types/element';
import { InterfaceActionsKeys } from '../types/interface';

const initState = () => {
  const state: SessionState = {
    sessionList: [],
    activeSession: null,
    users: [],
  };
  return state;
};

const Session: Module<SessionState, RootState> = {
  state: {
    ...initState(),
  },
  actions: {
    [SessionActionsKeys.LIST_SESSIONS]: async ({ commit }) => {
      const resp = await API.get('/api/sessions/list');
      commit(SessionMutationsKeys['SET_LIST_SESSIONS'], resp.data);
    },
    [SessionActionsKeys.BOARD_LIST_SESSIONS]: async (_, payload: SessionActionsPayload['BOARD_LIST_SESSIONS']) => {
      const resp = await API.get(`/api/sessions/${payload}`);
      return resp.data;
    },
    [SessionActionsKeys.CREATE_SESSION]: async (
      { commit, dispatch },
      payload: SessionActionsPayload['CREATE_SESSION']
    ) => {
      if (payload.boardId === null) {
        const board = await dispatch(BoardActionsKeys.CREATE_BOARD);
        payload.boardId = board.id;
      }
      const resp = await API.post('/api/session/create', payload);
      commit(SessionMutationsKeys['ADD_SESSION'], resp.data.session);
    },
    [SessionActionsKeys.LOAD_GUEST_SESSION]: async (
      { commit, dispatch },
      payload: SessionActionsPayload['LOAD_GUEST_SESSION']
    ) => {
      const resp = await API.get(`/api/session/pin/${payload}`);
      await dispatch(SessionActionsKeys.LEAVE_SESSION);
      commit(SessionMutationsKeys.LOAD_SESSION, resp.data.session);
      dispatch(BoardActionsKeys.OPEN_BOARD, { id: resp.data.session.board.id });
      dispatch(SessionActionsKeys.JOIN_SESSION, resp.data.session.pin);
    },
    [SessionActionsKeys.LOAD_SESSION]: async (
      { commit, dispatch, rootGetters },
      payload: SessionActionsPayload['LOAD_SESSION']
    ) => {
      const resp = await API.get(`/api/session/${payload}`);
      await dispatch(SessionActionsKeys.LEAVE_SESSION);
      commit(SessionMutationsKeys.LOAD_SESSION, resp.data.session);
      if ((rootGetters as RootGettersReturns).boardId !== resp.data.session.board.id) {
        dispatch(BoardActionsKeys.OPEN_BOARD, { id: resp.data.session.board.id });
      }
      dispatch(SessionActionsKeys.JOIN_SESSION, resp.data.session.pin);
    },
    [SessionActionsKeys.LEAVE_SESSION]: async ({ commit, dispatch, rootGetters }) => {
      const pin = (rootGetters as RootGettersReturns).sessionPin;
      if (!pin) return;
      const params: LeaveSessionEmit = {
        type: 'leave_session',
        params: [{ pin }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      commit(SessionMutationsKeys.LEAVE_SESSION);
      dispatch(InterfaceActionsKeys.CHANGE_LASER_ACTIVE, false);
    },
    [SessionActionsKeys.CHANGE_SESSION_STATUS]: (
      { commit },
      payload: SessionActionsPayload['CHANGE_SESSION_STATUS']
    ) => {
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, payload);
    },
    [SessionActionsKeys.JOIN_SESSION]: async (
      { state, commit, dispatch, rootGetters },
      payload?: SessionActionsPayload['JOIN_SESSION']
    ) => {
      const currentUser = (rootGetters as RootGettersReturns).currentUser;
      let pin = payload || state.activeSession?.pin;
      if (currentUser?.type === 'Guest') pin = pin || currentUser.session_pin;
      if (!pin) return;
      const params: JoinSessionEmit = {
        type: 'join_session',
        params: [{ pin }],
      };
      const { conference_ongoing, status, states, users } = (await dispatch(
        SocketActionsKeys.SOCKET_EMIT,
        params
      )) as JoinSessionResponse;
      commit(SessionMutationsKeys.JOIN_SESSION, pin);
      commit(JitsiMutationsKeys.SET_ONGOING_MEET, conference_ongoing);
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, status);
      commit(SessionMutationsKeys.RESET_USERS);
      dispatch(InterfaceActionsKeys.CHANGE_LASER_ACTIVE, false);
      users.forEach(u => {
        commit(SessionMutationsKeys.ADD_USER, u);
      });
      states.forEach(state => {
        if (state.type === StageDBType.Pointer) {
          commit(StageMutationsKeys.SET_POINTER, state.data);
        } else if (state.type === StageDBType.Focus) {
          const stageUpdate = states.find(state => state.type === StageDBType.Stage);
          if (stageUpdate && new Date(stageUpdate.updatedAt).getTime() < new Date(state.updatedAt).getTime()) {
            commit(StageActionsKeys.SET_FOCUS, state.data);
          }
        } else if (state.type === StageDBType.Stage) {
          const focusUpdate = states.find(state => state.type === StageDBType.Focus);
          if (focusUpdate && new Date(focusUpdate.updatedAt).getTime() < new Date(state.updatedAt).getTime()) {
            commit(StageActionsKeys.SET_STAGE, state.data);
          }
        }
      });
    },
    [SessionActionsKeys.START_SESSION]: async ({ commit, dispatch, rootGetters }) => {
      const pin = (rootGetters as RootGettersReturns).sessionPin;
      if (!pin) return;
      const params: StartSessionEmit = {
        type: 'start_session',
        params: [{ pin }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      commit(SessionMutationsKeys.RESET_USERS);
      const usersParams: SessionUsersEmit = {
        type: 'list_session_users',
        params: [{ pin }],
      };
      const { users } = (await dispatch(SocketActionsKeys.SOCKET_EMIT, usersParams)) as SessionUsersResponse;
      users.forEach(u => {
        commit(SessionMutationsKeys.ADD_USER, u);
      });
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, SessionStatus.STARTED);
    },
    [SessionActionsKeys.PAUSE_SESSION]: async ({ commit, dispatch, rootGetters }) => {
      const pin = (rootGetters as RootGettersReturns).sessionPin;
      if (!pin) return;
      const params: PauseSessionEmit = {
        type: 'pause_session',
        params: [{ pin }],
      };
      await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, SessionStatus.PAUSED);
      commit(JitsiMutationsKeys.STOP_MEET);
    },
    [SessionActionsKeys.DELETE_SESSION]: async (
      { commit, dispatch },
      payload: SessionActionsPayload['DELETE_SESSION']
    ) => {
      const { status } = await API.delete(`/api/session/${payload.id}`);
      if (status === 200) {
        commit(SessionMutationsKeys.DELETE_SESSION, payload);
        try {
          const params: DeleteSessionEmit = {
            type: 'delete_session',
            params: [{ pin: payload.pin }],
          };
          await dispatch(SocketActionsKeys.SOCKET_EMIT, params);
        } catch (e) {
          console.log('error', e);
        }
      }
    },
    [SessionActionsKeys.SOCKET_ON_STARTED_SESSION]: async (
      { commit, dispatch, rootGetters },
      { states }: SessionStartedResponse
    ) => {
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, SessionStatus.STARTED);
      states.forEach(state => {
        if (state.type === StageDBType.Pointer) {
          commit(StageMutationsKeys.SET_POINTER, state.data);
        } else if (state.type === StageDBType.Focus) {
          const stageUpdate = states.find(state => state.type === StageDBType.Stage);
          if (stageUpdate && new Date(stageUpdate.updatedAt).getTime() < new Date(state.updatedAt).getTime()) {
            commit(StageActionsKeys.SET_FOCUS, state.data);
          }
        } else if (state.type === StageDBType.Stage) {
          const focusUpdate = states.find(state => state.type === StageDBType.Focus);
          if (focusUpdate && new Date(focusUpdate.updatedAt).getTime() < new Date(state.updatedAt).getTime()) {
            commit(StageActionsKeys.SET_STAGE, state.data);
          }
        }
      });
      const pin = (rootGetters as RootGettersReturns).sessionPin;
      if (!pin) return;
      const isGuest = (rootGetters as RootGettersReturns).isGuest;
      if (isGuest) dispatch(ElementActionsKeys.LOAD_ELEMENTS);
      commit(SessionMutationsKeys.RESET_USERS);
      const usersParams: SessionUsersEmit = {
        type: 'list_session_users',
        params: [{ pin }],
      };
      const { users } = (await dispatch(SocketActionsKeys.SOCKET_EMIT, usersParams)) as SessionUsersResponse;
      users.forEach(u => {
        commit(SessionMutationsKeys.ADD_USER, u);
      });
    },
    [SessionActionsKeys.SOCKET_ON_PAUSED_SESSION]: ({ commit }) => {
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, SessionStatus.PAUSED);
    },
    [SessionActionsKeys.SOCKET_ON_DELETED_SESSION]: (
      { commit, rootGetters },
      payload: SessionActionsPayload['SOCKET_ON_DELETED_SESSION']
    ) => {
      const isGuest = (rootGetters as RootGettersReturns).isGuest;
      commit(SessionMutationsKeys.CHANGE_SESSION_STATUS, SessionStatus.DELETED);
      if (!isGuest) commit(SessionMutationsKeys.DELETE_SESSION, { pin: payload.session_pin });
    },
    [SessionActionsKeys.SOCKET_ON_JOINED_SESSION]: (
      { commit },
      payload: SessionActionsPayload['SOCKET_ON_JOINED_SESSION']
    ) => {
      commit(SessionMutationsKeys.ADD_USER, payload.user);
    },
    [SessionActionsKeys.SOCKET_ON_LEFT_SESSION]: (
      { dispatch, commit, rootGetters },
      payload: SessionActionsPayload['SOCKET_ON_LEFT_SESSION']
    ) => {
      if (!(rootGetters as RootGettersReturns).isGuest) {
        const elements = (rootGetters as RootGettersReturns).allElements.filter(el => {
          return (el.data as { usedBy: number }).usedBy === payload.user.id;
        });
        elements.forEach(el => {
          dispatch(ElementActionsKeys.UPDATE_ELEMENT, {
            ...el,
            data: {
              ...el.data,
              usedBy: false,
            },
          });
        });
      }
      commit(SessionMutationsKeys.REMOVE_USER, payload.user);
    },
    [SessionActionsKeys.RESET_STATE]: ({ commit }) => {
      commit(SessionMutationsKeys.SESSION_RESET_STATE);
    },
  },
  mutations: {
    [SessionMutationsKeys.SET_LIST_SESSIONS]: (state, payload: SessionMutationsPayload['SET_LIST_SESSIONS']) => {
      state.sessionList = payload.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());
    },
    [SessionMutationsKeys.ADD_SESSION]: (state, payload: SessionMutationsPayload['ADD_SESSION']) => {
      state.sessionList = [payload, ...state.sessionList];
    },
    [SessionMutationsKeys.LOAD_SESSION]: (state, payload: SessionMutationsPayload['LOAD_SESSION']) => {
      state.activeSession = payload;
    },
    [SessionMutationsKeys.LEAVE_SESSION]: state => {
      state.activeSession = null;
    },
    [SessionMutationsKeys.REMOVE_SESSIONS_FOR_BOARD]: (
      state,
      payload: SessionMutationsPayload['REMOVE_SESSIONS_FOR_BOARD']
    ) => {
      state.sessionList = [...state.sessionList.filter(s => s.board.id !== payload)];
    },
    [SessionMutationsKeys.DELETE_SESSION]: (state, payload: SessionMutationsPayload['DELETE_SESSION']) => {
      state.sessionList = [...state.sessionList.filter(s => s.pin !== payload.pin)];
      if (state.activeSession?.pin === payload.pin) {
        state.activeSession = null;
      }
    },
    [SessionMutationsKeys.ADD_USER]: (state, payload: SessionMutationsPayload['ADD_USER']) => {
      const user = state.users.find(u => u.id === payload.id);
      if (user) return;
      state.users = [...state.users, payload];
    },
    [SessionMutationsKeys.REMOVE_USER]: (state, payload: SessionMutationsPayload['REMOVE_USER']) => {
      state.users = state.users.filter(u => u.id !== payload.id);
    },
    [SessionMutationsKeys.RESET_USERS]: state => {
      state.users = [];
    },
    [SessionMutationsKeys.JOIN_SESSION]: (state, payload: SessionMutationsPayload['JOIN_SESSION']) => {
      if (state.activeSession) state.activeSession.pin = payload;
    },
    [SessionMutationsKeys.CHANGE_SESSION_STATUS]: (
      state,
      payload: SessionMutationsPayload['CHANGE_SESSION_STATUS']
    ) => {
      if (state.activeSession) state.activeSession.status = payload;
    },
    [SessionMutationsKeys.SESSION_RESET_STATE]: state => {
      Object.assign(state, initState());
    },
  },
  getters: {
    [SessionGettersKeys.sessionPin]: state => state.activeSession?.pin,
    [SessionGettersKeys.sessionStatus]: state => state.activeSession?.status,
    [SessionGettersKeys.sessionJoined]: state => !!state.activeSession,
    [SessionGettersKeys.sessionExternalLink]: state => state.activeSession?.externalConferenceLink,
    [SessionGettersKeys.sessionList]: state => state.sessionList,
    [SessionGettersKeys.sessionUsers]: state => state.users,
  },
  modules: {
    Jitsi,
  },
};

export default Session;
